import { useSafeContext } from 'hooks/context.hooks';
import { useController, useFieldArray } from 'react-hook-form';
import { daysOfWeekOptionsLong } from '../days-multiple-select-field/days-multiple-select-field.utils';
import {
  AdditionalData,
  Schedule,
  SchedulePayload,
  SchedulerContext,
  SchedulerContextType,
} from './scheduler-field.context';
import { schedulerToolbarFormSchema } from './scheduler-toolbar-form/scheduler-toolbar-form.schema';

export const useSchedulerField = ({
  name,
  timeFormat = 'HH:mm',
  compact = false,
  additionalData,
  schema,
}: {
  name: string;
  timeFormat?: string;
  compact?: boolean;
  additionalData?: AdditionalData;
  schema?: typeof schedulerToolbarFormSchema;
}): SchedulerContextType => {
  const { fieldState } = useController({ name });
  const fieldArray = useFieldArray({ name });

  const addSchedule = (schedulePayload: SchedulePayload) => {
    let { daysOfWeek, timeRange, ...rest } = schedulePayload;
    if (daysOfWeek.some(day => day.id === 'ALL')) {
      daysOfWeek = daysOfWeekOptionsLong().filter(
        option => option.id !== 'ALL',
      );
    }
    const transformedScheduleItem = daysOfWeek.map(dayOfWeek => {
      const { id } = dayOfWeek;
      const startTime = timeRange[0].toFormat(timeFormat);
      const endTime = timeRange[1].toFormat(timeFormat);

      return {
        day: id,
        startTime,
        endTime,
        ...rest,
      };
    });

    transformedScheduleItem.forEach(item => fieldArray.append(item));
  };

  const removeSchedule = (scheduleToRemove: Schedule) => {
    fieldArray.remove(
      (fieldArray.fields as unknown as Schedule[]).findIndex(
        item =>
          item.day === scheduleToRemove.day &&
          item.startTime === scheduleToRemove.startTime &&
          item.endTime === scheduleToRemove.endTime,
      ),
    );
  };

  return {
    addSchedule,
    removeSchedule,
    timeFormat,
    compact,
    additionalData,
    schedules: fieldArray.fields as unknown as Schedule[],
    fieldState,
    schema,
  };
};

export const useSchedulerContext = () => useSafeContext(SchedulerContext);
